import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getKycLibraryTemplates } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import type { UseTemplatePickerArgs } from './useTemplatePicker.types';

export const useTemplatePicker = ({
	opened,
	investorType,
	category,
	onAdd,
	onClose,
}: UseTemplatePickerArgs) => {
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [isAdding, setIsAdding] = useState(false);

	const { data: templates, isLoading } = useQuery({
		queryKey: [
			API_PATHS.DOCUMENT_WORKFLOW_KYC_FORM_TEMPLATES_LIBRARY_GET,
			investorType,
			category,
		],
		queryFn: () => getKycLibraryTemplates({ type: investorType, category }),
		// The modal is also mounted only while open, so this is belt-and-braces — it keeps the
		// hook correct if it ever becomes always-mounted, as Mantine's own pattern would have it.
		enabled: opened,
	});

	const close = () => {
		setSelectedId(null);
		onClose();
	};

	const add = async () => {
		const selected = (templates ?? []).find((template) => template.id === selectedId);
		if (selected === undefined) {
			return;
		}
		setIsAdding(true);
		try {
			await onAdd(selected);
			close();
		} finally {
			setIsAdding(false);
		}
	};

	return {
		templates: templates ?? [],
		isLoading,
		isAdding,
		selectedId,
		setSelectedId,
		add,
		close,
	};
};
